import { useState, useEffect } from 'react';
import { ShieldCheck, ChevronDown } from 'lucide-react';
import { adminAxios } from './AdminLayout';
import { ComplianceReviewPanel, ComplianceBadge, COMPLIANCE_STATUSES } from '../../components/admin/ComplianceReviewPanel';

export default function AdminCompliance() {
  var [cases, setCases] = useState([]);
  var [stats, setStats] = useState(null);
  var [filter, setFilter] = useState('');
  var [includeNotRequired, setIncludeNotRequired] = useState(false);
  var [expandedId, setExpandedId] = useState(null);
  var [loading, setLoading] = useState(true);

  function fetchAll() {
    var url = '/api/admin/compliance/cases?include_not_required=' + includeNotRequired + (filter ? '&status=' + filter : '');
    adminAxios().get(url).then(function(r) { setCases(r.data); setLoading(false); });
    adminAxios().get('/api/admin/compliance/stats').then(function(r) { setStats(r.data); });
  }
  useEffect(function() { fetchAll(); }, [filter, includeNotRequired]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="admin-compliance-page">
      <div className="flex items-start justify-between gap-4 mb-2">
        <div>
          <h2 className="font-outfit text-2xl font-bold text-slate-900">Export Compliance</h2>
          <p className="font-manrope text-sm text-slate-500 mt-1">Transaction-level review of enquiries for export-controlled hardware. Independent from campaign recipients and market segmentation.</p>
        </div>
        <ShieldCheck className="w-8 h-8 text-[#0096D6] shrink-0" />
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 my-6" data-testid="compliance-stats">
          <div className="bg-white rounded-2xl border border-amber-100 p-4 shadow-sm text-center"><p className="font-space text-2xl font-bold text-amber-700">{stats.open}</p><p className="font-manrope text-[10px] text-amber-700">Open cases</p></div>
          <div className="bg-white rounded-2xl border border-blue-100 p-4 shadow-sm text-center"><p className="font-space text-2xl font-bold text-blue-600">{stats.counts.IN_REVIEW}</p><p className="font-manrope text-[10px] text-blue-600">In review</p></div>
          <div className="bg-white rounded-2xl border border-green-100 p-4 shadow-sm text-center"><p className="font-space text-2xl font-bold text-green-600">{stats.counts.CLEARED + stats.counts.CLEARED_WITH_CONDITIONS}</p><p className="font-manrope text-[10px] text-green-600">Cleared</p></div>
          <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm text-center"><p className="font-space text-2xl font-bold text-slate-900">{stats.counts.LICENCE_REQUIRED + stats.counts.DECLINED}</p><p className="font-manrope text-[10px] text-slate-500">Licence / declined</p></div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button onClick={function(){ setFilter(''); }} className={'px-3 py-1.5 rounded-full font-manrope text-xs font-medium ' + (!filter ? 'bg-[#0096D6] text-white' : 'bg-slate-100 text-slate-600')} data-testid="compliance-filter-all">All</button>
        {COMPLIANCE_STATUSES.filter(function(s){ return s !== 'NOT_REQUIRED'; }).map(function(s) {
          return <button key={s} onClick={function(){ setFilter(s); }} className={'px-3 py-1.5 rounded-full font-manrope text-xs font-medium ' + (filter === s ? 'bg-[#0096D6] text-white' : 'bg-slate-100 text-slate-600')} data-testid={'compliance-filter-' + s.toLowerCase()}>{s.replace(/_/g, ' ')}{stats ? ' (' + stats.counts[s] + ')' : ''}</button>;
        })}
        <label className="ml-auto flex items-center gap-2 font-manrope text-xs text-slate-500 cursor-pointer">
          <input type="checkbox" checked={includeNotRequired} onChange={function(e){ setIncludeNotRequired(e.target.checked); setFilter(''); }} className="w-3.5 h-3.5 rounded border-slate-300" data-testid="compliance-include-not-required" />
          Show not-required
        </label>
      </div>

      {loading ? <div className="font-manrope text-slate-500">Loading...</div> :
       cases.length === 0 ? <div className="text-center py-12 bg-white rounded-2xl border border-slate-100" data-testid="compliance-empty"><ShieldCheck className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="font-manrope text-slate-500">No compliance cases</p></div> :
       <div className="space-y-3">
        {cases.map(function(c) {
          var key = c.source + '-' + c.id;
          var expanded = expandedId === key;
          return (
            <div key={key} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid={'compliance-case-' + c.id}>
              <div className="p-5 flex items-center justify-between cursor-pointer" onClick={function(){ setExpandedId(expanded ? null : key); }}>
                <div>
                  <h4 className="font-manrope font-semibold text-slate-900">{c.company || '(no company)'} — {c.contact}</h4>
                  <p className="font-manrope text-sm text-slate-500 mt-0.5">{c.products}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <ComplianceBadge status={c.compliance_status} testId={'compliance-list-status-' + c.id} />
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-500 uppercase">{c.source === 'quote' ? 'Quote request' : 'Website enquiry'}</span>
                    {c.country && <span className="font-manrope text-xs text-slate-400">{c.country}</span>}
                    <span className="font-manrope text-xs text-slate-400">{c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}</span>
                  </div>
                </div>
                <ChevronDown className={'w-5 h-5 text-slate-400 transition-transform ' + (expanded ? 'rotate-180' : '')} />
              </div>
              {expanded && (
                <div className="px-5 pb-5 border-t border-slate-100 pt-4">
                  <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                    <div><span className="font-manrope text-xs text-slate-500">Email:</span> <span className="font-manrope text-slate-900">{c.email}</span></div>
                    <div><span className="font-manrope text-xs text-slate-500">Commercial status:</span> <span className="font-manrope text-slate-900">{c.commercial_status || '—'}</span></div>
                  </div>
                  {c.message && <div className="mb-4 p-3 bg-slate-50 rounded-lg"><span className="font-manrope text-xs text-slate-500">Message / comments:</span><p className="font-manrope text-sm text-slate-700 mt-1">{c.message}</p></div>}
                  <ComplianceReviewPanel data={c} source={c.source} caseId={c.id} onChanged={fetchAll} forceShow />
                </div>
              )}
            </div>
          );
        })}
       </div>
      }
    </div>
  );
}
